import { type ReloginSecret, saveSecret } from "@/unlock";
import { useEffect, useState } from "react";
import { PIN_LENGTH, PinPad } from "./PinPad";

const WEAK_MESSAGE = "Elegí un PIN menos previsible: ni seis dígitos iguales ni una seguidilla.";

function isWeak(pin: string): boolean {
  if (new Set(pin).size === 1) {
    return true;
  }
  const digits = [...pin].map(Number);
  const first = digits[0] ?? 0;
  const ascending = digits.every((digit, index) => digit === first + index);
  const descending = digits.every((digit, index) => digit === first - index);
  return ascending || descending;
}

export function PinCreate({
  ownerKey,
  secret,
  label,
  onDone,
  onCancel,
}: {
  ownerKey: string;
  secret: ReloginSecret;
  label: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pin.length < PIN_LENGTH || busy) {
      return;
    }
    if (chosen === null) {
      if (isWeak(pin)) {
        setError(WEAK_MESSAGE);
        setPin("");
        return;
      }
      setChosen(pin);
      setPin("");
      setError(null);
      return;
    }
    if (pin !== chosen) {
      setError("Los dos PIN no coinciden. Empezá de nuevo.");
      setChosen(null);
      setPin("");
      return;
    }
    setBusy(true);
    saveSecret(ownerKey, chosen, secret, label)
      .then(onDone)
      .catch(() => {
        setError("No pudimos guardar el PIN en este teléfono. Probá de nuevo.");
        setChosen(null);
        setPin("");
      })
      .finally(() => setBusy(false));
  }, [pin, chosen, busy, ownerKey, secret, label, onDone]);

  return (
    <div className="pin-flow">
      <h2 className="t-h3">{chosen === null ? "Elegí tu PIN" : "Repetilo"}</h2>
      <p className="t-small">
        {chosen === null
          ? "Seis dígitos que puedas tocar con una mano."
          : "Escribilo otra vez para que no te quede uno con un dedazo."}
      </p>

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}

      <PinPad value={pin} onChange={setPin} disabled={busy} />

      <button type="button" className="btn btn--block" onClick={onCancel} disabled={busy}>
        Cancelar
      </button>
    </div>
  );
}
